import { AsyncSubject, type Observable } from 'rxjs';
import axios, {
  AxiosError,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
  type AxiosResponse,
  type Method,
} from 'axios';
import authService from './authService';

interface HttpRequest {
  method: Method;
  url: string;
  headers?: AxiosRequestHeaders;
  data?: any;
  responseType?: 'json' | 'arraybuffer';
}

class HttpService {
  public getJSON(url: string): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'GET',
      url,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public postJSON(
    url: string,
    data: any,
    formData: boolean = false,
    responseType: 'json' | 'arraybuffer' = 'json',
  ): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'POST',
      url,
      data,
      responseType,
    };
    this.makeRequest(request, subject, formData);

    return subject;
  }

  public putJSON(url: string, data: any): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'PUT',
      url,
      data,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public patchJSON(url: string, data: any): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'PATCH',
      url,
      data,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public deleteJSON(url: string): Observable<any> {
    const subject = new AsyncSubject<any>();

    const request: HttpRequest = {
      method: 'DELETE',
      url,
    };
    this.makeRequest(request, subject);

    return subject;
  }

  public getQueryParams(
    params: Record<
      string,
      string | string[] | number | boolean | undefined | null
    >,
  ): string {
    return Object.entries(params)
      .filter(
        ([_, value]) =>
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0),
      )
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${encodeURIComponent(key)}=${encodeURIComponent(
            (value as string[]).join(','),
          )}`;
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(
          value?.toString() ?? '',
        )}`;
      })
      .join('&');
  }

  private makeRequest(
    request: HttpRequest,
    subject: AsyncSubject<AxiosResponse>,
    formData: boolean = false,
  ) {
    this.getOptions(request, formData).then((options) => {
      axios
        .request(options)
        .then((resp) => {
          this.resolveRequest(resp, subject);
        })
        .catch((err) => {
          if (err.response?.status === 401) {
            authService.logout();
          }
          this.resolveRequest(err, subject);
        });
    });
  }

  private resolveRequest(response: AxiosResponse, subject: AsyncSubject<any>) {
    if (this.isValid(response)) {
      const data = response.data.data ? response.data.data : response.data;
      subject.next(data);
      subject.complete();
    } else {
      const err = response as unknown as AxiosError;
      subject.error(err.response?.data ?? response.data ?? response);
    }
  }

  private getOptions(
    request: HttpRequest,
    formData: boolean = false,
  ): Promise<AxiosRequestConfig> {
    return new Promise((resolve) => {
      this.getHeaders(formData, request.responseType).then((headers) => {
        const options: AxiosRequestConfig = {
          method: request.method,
          url: request.url,
          headers,
          data: request.data ?? undefined,
          responseType: request.responseType,
        };
        resolve(options);
      });
    });
  }

  private getHeaders(formData: boolean, responseType?: string): Promise<any> {
    const { __environment__ } = window as any;

    return new Promise((done) => {
      const newHeaders = {
        Accept:
          responseType === 'arraybuffer'
            ? 'application/pdf'
            : 'application/json',
        'X-App-Bc': __environment__.REACT_APP_CLIENT_BC?.trim(),
        'X-Address-Validation-Bc': __environment__.REACT_APP_CLIENT_BC?.trim(),
        'Content-Type': formData
          ? 'multipart/form-data'
          : 'application/json;charset=UTF-8',
      };

      authService.getTokens().subscribe((tokens) => {
        if (tokens.accessToken) {
          (newHeaders as any).Authorization = `Bearer ${tokens.accessToken}`;
        }
        if (tokens.idToken) {
          (newHeaders as any).AuthorizationID = `${tokens.idToken}`;
        }

        done(newHeaders);
      });

      authService.getTier3Tokens().subscribe((tokens) => {
        if (tokens.accessToken) {
          (newHeaders as any).Authorization = `Bearer ${tokens.accessToken}`;
        }
        if (tokens.idToken) {
          (newHeaders as any).AuthorizationID = `${tokens.idToken}`;
        }

        done(newHeaders);
      });
    });
  }

  private isValid(response: AxiosResponse): boolean {
    return response.status >= 200 && response.status < 300;
  }
}

const httpService = new HttpService();

export default httpService;
